import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useApp } from '@/context/AppContext';
import { SentSyncWidget } from './SentSyncWidget';
import { PROVIDERS, inputClass, inputStyle } from './constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AccountTab() {
  const navigate = useNavigate();
  const { accounts, addAccount, deleteAccount } = useApp();
  const [showAddForm, setShowAddForm] = useState(accounts.length === 0);
  const [form, setForm] = useState({ email: '', password: '', imap_server: '', imap_port: 993, smtp_server: '', smtp_port: 465 });
  const [selectedProvider, setSelectedProvider] = useState('');
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [testResults, setTestResults] = useState({});
  const field = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleProviderSelect = (name) => {
    setSelectedProvider(name);
    const p = PROVIDERS.find(p => p.name === name);
    if (p) setForm(prev => ({ ...prev, imap_server: p.imap_server, imap_port: p.imap_port, smtp_server: p.smtp_server, smtp_port: p.smtp_port }));
  };

  const handleAdd = async () => {
    if (!form.email || !form.password || !form.imap_server || !form.smtp_server) { toast.error('Compila tutti i campi obbligatori'); return; }
    setAdding(true);
    try {
      await addAccount(form);
      toast.success(`Account ${form.email} aggiunto`);
      setForm({ email: '', password: '', imap_server: '', imap_port: 993, smtp_server: '', smtp_port: 465 });
      setSelectedProvider('');
      setShowAddForm(false);
      navigate('/dashboard');
    } catch (e) {
      toast.error(`Errore: ${e.response?.data?.detail || e.message}`);
    } finally { setAdding(false); }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Eliminare l'account ${email}?`)) return;
    setDeletingId(id);
    try { await deleteAccount(id); toast.success('Account eliminato'); }
    catch { toast.error("Errore nell'eliminazione"); }
    finally { setDeletingId(null); }
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const res = await axios.post(`${API}/accounts/${id}/test`);
      setTestResults(prev => ({ ...prev, [id]: res.data }));
    } catch { toast.error('Errore nel test'); }
    finally { setTestingId(null); }
  };

  return (
    <div className="space-y-6">
      {accounts.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            Account configurati
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(226,114,91,0.15)', color: '#E2725B' }}>{accounts.length}</span>
          </h3>
          <div className="space-y-3">
            {accounts.map(acc => {
              const result = testResults[acc.id];
              return (
                <div key={acc.id} data-testid={`account-item-${acc.id}`} className="rounded-xl border p-4" style={{ borderColor: '#D9D2C8', background: 'white', borderLeft: `3px solid ${acc.color}` }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full" style={{ background: acc.color }} />
                      <div>
                        <p className="text-sm font-medium">{acc.email}</p>
                        <p className="text-xs text-gray-400">IMAP: {acc.imap_server}:{acc.imap_port} &bull; SMTP: {acc.smtp_server}:{acc.smtp_port}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button data-testid={`test-connection-${acc.id}`} onClick={() => handleTest(acc.id)} disabled={testingId === acc.id} className="text-xs px-3 py-1.5 border rounded-lg hover:bg-gray-50 transition-all" style={{ borderColor: '#D9D2C8' }}>
                        {testingId === acc.id ? <Loader2 size={12} className="animate-spin" /> : 'Test'}
                      </button>
                      <button data-testid={`delete-account-${acc.id}`} onClick={() => handleDelete(acc.id, acc.email)} disabled={deletingId === acc.id} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg">
                        {deletingId === acc.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </div>
                  {result && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg ${result.imap.ok ? 'text-green-600' : 'text-red-500'}`} style={{ background: result.imap.ok ? '#E4F7EE' : '#FFE8E8' }}>
                        {result.imap.ok ? <CheckCircle size={12} /> : <XCircle size={12} />} IMAP: {result.imap.ok ? 'OK' : result.imap.error}
                      </div>
                      <div className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg ${result.smtp.ok ? 'text-green-600' : 'text-red-500'}`} style={{ background: result.smtp.ok ? '#E4F7EE' : '#FFE8E8' }}>
                        {result.smtp.ok ? <CheckCircle size={12} /> : <XCircle size={12} />} SMTP: {result.smtp.ok ? 'OK' : result.smtp.error}
                      </div>
                    </div>
                  )}
                  <SentSyncWidget accountId={acc.id} />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {!showAddForm ? (
        <button data-testid="show-add-account-btn" onClick={() => setShowAddForm(true)} className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-2 border-dashed rounded-xl transition-all hover:border-solid" style={{ borderColor: '#E2725B', color: '#E2725B' }}>
          <Plus size={16} /> Aggiungi account email
        </button>
      ) : (
        <section data-testid="add-account-form" className="rounded-xl border p-6" style={{ borderColor: '#D9D2C8', background: 'white' }}>
          <h3 className="text-sm font-semibold mb-4">Aggiungi account email</h3>
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Provider</label>
            <div className="grid grid-cols-3 gap-2">
              {PROVIDERS.map(p => (
                <button key={p.name} onClick={() => handleProviderSelect(p.name)} className="px-3 py-2 text-xs font-medium border rounded-lg transition-all text-left" style={{ borderColor: selectedProvider === p.name ? '#E2725B' : '#D9D2C8', background: selectedProvider === p.name ? 'rgba(226,114,91,0.08)' : 'transparent', color: selectedProvider === p.name ? '#E2725B' : '#333' }}>
                  {p.name}
                </button>
              ))}
            </div>
            {selectedProvider && <p className="mt-2 text-xs text-gray-400 p-2 rounded-lg bg-gray-50">{PROVIDERS.find(p => p.name === selectedProvider)?.note}</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-gray-400 mb-1">Email *</label><input data-testid="add-account-email" type="email" value={form.email} onChange={e => field('email', e.target.value)} placeholder="nome@esempio.com" className={inputClass} style={inputStyle} /></div>
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-gray-400 mb-1">Password *</label><input data-testid="add-account-password" type="password" value={form.password} onChange={e => field('password', e.target.value)} placeholder="Password o App Password" className={inputClass} style={inputStyle} /></div>
            <div><label className="block text-xs font-medium text-gray-400 mb-1">Server IMAP *</label><input data-testid="add-account-imap-server" type="text" value={form.imap_server} onChange={e => field('imap_server', e.target.value)} placeholder="imap.esempio.com" className={inputClass} style={inputStyle} /></div>
            <div><label className="block text-xs font-medium text-gray-400 mb-1">Porta IMAP</label><input data-testid="add-account-imap-port" type="number" value={form.imap_port} onChange={e => field('imap_port', parseInt(e.target.value))} className={inputClass} style={inputStyle} /></div>
            <div><label className="block text-xs font-medium text-gray-400 mb-1">Server SMTP *</label><input data-testid="add-account-smtp-server" type="text" value={form.smtp_server} onChange={e => field('smtp_server', e.target.value)} placeholder="smtp.esempio.com" className={inputClass} style={inputStyle} /></div>
            <div><label className="block text-xs font-medium text-gray-400 mb-1">Porta SMTP</label><input data-testid="add-account-smtp-port" type="number" value={form.smtp_port} onChange={e => field('smtp_port', parseInt(e.target.value))} className={inputClass} style={inputStyle} /></div>
          </div>
          <div className="flex items-center gap-3 mt-5">
            <button data-testid="add-account-submit-btn" onClick={handleAdd} disabled={adding} className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-xl transition-all" style={{ background: '#E2725B', opacity: adding ? 0.7 : 1 }}>
              {adding ? <><Loader2 size={13} className="animate-spin" />Connessione...</> : <><ArrowRight size={13} />Aggiungi account</>}
            </button>
            {accounts.length > 0 && <button onClick={() => setShowAddForm(false)} className="px-4 py-2.5 text-sm border rounded-xl hover:bg-gray-50 transition-all" style={{ borderColor: '#D9D2C8' }}>Annulla</button>}
          </div>
          {adding && <p className="mt-3 text-xs text-gray-400 flex items-center gap-2"><Loader2 size={11} className="animate-spin" />Test connessione in corso...</p>}
        </section>
      )}
    </div>
  );
}
